import React from 'react';
import {GamesListBaseModel} from "./games-list-base.model";
import {
    ApiListType,
    SwiperGamesListConfiguration
} from "../../../configurations/config-data-sections/arenas.configuration";
import {IArenaToGamesListAdapter} from "../arenas/arena-to-games-list-adapter.interface";
import {EXCLUSIVES_LIST_ICON, JACKPOTS_ITEM} from "../../../../themes/css-global-classes";
import {TextTranslation} from "../../../translation/text-translation";
import {ListTitleIconBox} from "../../../../components/game-list-title/boxes/list-title-icon/list-title-icon.box";
import {Check} from "@sparkware/app-utils";
import {JackpotWidgetComponent} from "../../../../components/jackpot-widget/jackpot-widget.component";
import {JackpotWidgetWrapperBox} from "../../../../components/orbit-swiper/boxes/jackpot-widget-wrapper/jackpot-widget-wrapper.box";
import {RedTigerWidgetComponent} from "../../../../components/red-tiger-widget/red-tiger-widget.component";
import {ISwiperListViewModel} from "./swiper-list-view-model.interface";
import {JACKPOT_WIDGET, RED_TIGER_JACKPOT} from "../../../../components/game-list-title/constants";
import {ListTitleTextBox} from "../../../../components/game-list-title/boxes/list-title-text/list-title-text.box";
import {GameListTitleComponent} from "../../../../components/game-list-title/game-list-title.component";
import classNames from "classnames";
import {OrbitSwiperButtons} from "../../../../components/orbit-swiper/orbit-swiper-buttons/orbit-swiper-buttons";
import {SwipeLogicHandler} from "../swiper-logic-handler/swipe-logic-handler";
import {ISwipeLogicHandler} from "../swiper-logic-handler/swiper-logic-handler.interface";
import { JackpotType } from '../../../../../common/const/jackpot_map_ids';

export abstract class SwiperListBaseModel extends GamesListBaseModel implements ISwiperListViewModel {

    constructor(protected readonly listConfiguration: SwiperGamesListConfiguration, arena: IArenaToGamesListAdapter) {
        super(arena);
    }

    get listId(): number {
        return this.listConfiguration.listId;
    }

    get apiListType(): ApiListType | undefined {
        return this.listConfiguration.apiListType;
    }

    get title(): TextTranslation {
        return this.services.translation.getTranslation(this.listConfiguration.title, this._getTitleTranslationParams());
    }

    get swipeLogicHandler() {
        return this._swipeLogicHandler;
    }

    get showGameTitleOnMultipleLines() {
        const widgetConfig = this.listConfiguration.jackpotWidget;
		if (!widgetConfig || !widgetConfig.display) {
			return false;
		}

        return widgetConfig.jackpotType === JackpotType.HourlyCaptain8;
    }

    protected _swipeLogicHandler: ISwipeLogicHandler = new SwipeLogicHandler(this.services, this);

    protected _getTitleTranslationParams(): object | string | undefined {
        return this.listConfiguration.shouldInjectUsernameInTitle ? this.services.user.name : undefined;
    }

    protected get _numberOfGamesToBeIgnoredInAISorting(): number | undefined {
        return this.listConfiguration.ignoreNumberOfGamesInAISorting;
    }

    get listTypeId(): number | null {
        return null;
    }

    protected get _seeAllUrl(): string {
        const seeAllConfig = this.listConfiguration.seeAll || '';

        /**
         * We used to support SeeAll Button as an object, from 6.6.21 we've changed that to string, this is backward compatibility
         * Task https://dev.azure.com/888holdings/GamingNDL/_workitems/edit/1737512
         */
        if (Check.isObject(seeAllConfig)) {
            return (seeAllConfig as any).url || '';
        }

        return seeAllConfig;
    }

    protected get _isAISorted(): boolean {
        return this.listConfiguration.useAISortedList;
    }

    protected _shouldBeDisplayed(): boolean {
        return !this.services.gamesFilter.isFilterOn;
    }

    protected _getGameTypes(): number[] {
        const gameTypes = this.listConfiguration.gamesId || [];
        if (this.services.gamesFilter.isFilterOn) {
            return [];
        }

        return this.services.abFiltering.filterGamesByAbTesting(this.listConfiguration.gamesAdminRefKey, gameTypes);
    }


    protected _getNumberOfGamesSkeletons(): number {
        if (this.listConfiguration.jackpotWidget) {
            return 4;
        }

        if (this.listConfiguration.templateType === JACKPOTS_ITEM) {
            return 2;
        }

        return 6;
    }

    get numberOfGamesSkeletons(): number {
        return this._getNumberOfGamesSkeletons();
    }

    protected _reduceGameTypes(gameTypes: number[]): number[] {
        gameTypes = super._reduceGameTypes(gameTypes);
        return this._reduceGameTypesForJackpotsItemTemplateInHomePage(gameTypes);
    }

    private _reduceGameTypesForJackpotsItemTemplateInHomePage(gameTypes: number[]): number[] {
        if (!this.services.device.isMobile) {
            return gameTypes;
        }
        if (this.listConfiguration.templateType !== JACKPOTS_ITEM) {
            return gameTypes;
        }
        if (!this.services.location.isHomePage) {
            return gameTypes;
        }
        return gameTypes.slice(0, 6);
    }

    get forceInLayout(): boolean {
        return this.listConfiguration.forceListInLayout;
    }

    private get _isJackpotWidgetHost(): boolean {
        return Boolean(this.listConfiguration.jackpotWidget?.display);
    }

    private get _isRedTigerJackpotWidgetHost(): boolean {
        return Boolean(this.listConfiguration.redTigerWidget?.display);
    }

    get templateType(): string {
        return this.listConfiguration.templateType;
    }

    protected _renderJackpotWidget() {
        let onClickHandler = null;
        if(this.services.location.isHomePage) {
            onClickHandler = () => this.services.location.push('/jackpots/');
        }

        return (<JackpotWidgetComponent
            widgetConfig={this.listConfiguration.jackpotWidget}
            seoCategory="List_Widget"
            WrapperBox={JackpotWidgetWrapperBox}
            onClick={onClickHandler}
        />)
    }

    protected _renderRedTigerWidget() {
        return (
            <RedTigerWidgetComponent
                widgetConfig={this.listConfiguration.redTigerWidget}
            />
        );
    }

    renderSpecialWidgets(): React.ReactElement | null {
        return (
            <>
                {this._renderJackpotWidget()}
                {this._renderRedTigerWidget()}
            </>
        );
    }

    renderListHeader(): React.ReactElement {
        return (
            <GameListTitleComponent title={this._renderHeaderIconAndText()}
                                    className={classNames(...this._getTitleCssClasses())}>
                {this._renderSwiperButtons()}
            </GameListTitleComponent>
        )
    }

    protected _renderSwiperButtons() {
        return <OrbitSwiperButtons
            analyticsAction={this.title.translationKey}
            swiper={this.swipeLogicHandler}
            seeAllButtonUrl={this._seeAllUrl}
            seoCategory={this.arenaName}
        />;
    }


    _getTitleCssClasses(): string[] {
        const cssClasses: string[] = [this.listConfiguration.titleIconClassName];
        if(this._isJackpotWidgetHost) {
            cssClasses.push(JACKPOT_WIDGET);
        }

        if(this._isRedTigerJackpotWidgetHost) {
            cssClasses.push(RED_TIGER_JACKPOT)
        }

        return cssClasses;
    }

    _renderHeaderIconAndText(): React.ReactElement {
        return (
            <ListTitleTextBox className="cy-games-title-text">
                {this._renderHeaderIcon()}
                {this._renderHeaderText()}
            </ListTitleTextBox>
        )
    }

    protected _renderHeaderIcon(): React.ReactElement | null {
        if (this._isJackpotWidgetHost || this.listConfiguration.titleIconClassName === EXCLUSIVES_LIST_ICON) {
            return <ListTitleIconBox/>;
        }

        return null;
    }

    protected _renderHeaderText(): string | React.ReactElement {
        return this.title.translation
    }

    renderListNavigation(): React.ReactElement | null {
        return null;
    }

    renderListFooter(): React.ReactElement | null {
        return null;
    }

}
